import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Sun, Moon, Menu, X, Flame, Heart } from "lucide-react";
import api from "@/lib/api";
import type { User } from "@/contexts/AuthContext";

interface NavLinkDef {
  to: string;
  label: string;
}

const NAV_LINKS: NavLinkDef[] = [
  { to: "/", label: "Início" },
  { to: "/como-funciona", label: "Como Funciona" },
  { to: "/artigos", label: "Artigos" },
  { to: "/fale-conosco", label: "Fale Conosco" },
];

export default function Navbar(): React.JSX.Element {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [favoritesCount, setFavoritesCount] = useState(0);

  const isLoggedIn = user && typeof user === "object";
  const isAdminAccount = isLoggedIn && (user as User).role === "admin";
  const showPublishCta = !isAdminAccount;
  const showDashboardLink = isLoggedIn && !isAdminAccount;

  useEffect(() => {
    const fetchFavoritesCount = async () => {
      try {
        const { data } = await api.get("/favorites/me");
        setFavoritesCount(Array.isArray(data) ? data.length : 0);
      } catch {
        // Silently fail
      }
    };
    if (isLoggedIn) {
      fetchFavoritesCount();
    }
  }, [isLoggedIn]);
  return (
    <header className="glass-nav">
      <div className="container-custom h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-heat-grad-deep text-white shadow-heat-glow group-hover:shadow-heat-glow-lg transition-shadow">
            <Flame className="w-4 h-4" aria-hidden="true" />
          </span>
          <span className="font-display font-semibold text-xl tracking-tight text-ink-900">
            forfada<span className="text-heat-500">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-tight link-underline transition-colors ${
                  isActive ? "text-heat-700" : "text-ink-700 hover:text-heat-700"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2.5 px-3 py-2 rounded-full hover:bg-cream-100 transition-colors text-ink-700 hover:text-heat-700"
            aria-label="Alternar tema"
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            <span className="text-[10px] font-extrabold uppercase tracking-widest">Alternar Cor</span>
          </button>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              {isAdminAccount && (
                <Link to="/admin" className="text-sm font-semibold text-heat-700 hover:text-heat-800" data-testid="nav-admin">
                  Admin
                </Link>
              )}
              {showDashboardLink && (
                <Link to="/dashboard" className="text-sm font-semibold text-ink-700 hover:text-heat-700" data-testid="nav-dashboard">
                  Painel
                </Link>
              )}
              <Link to="/dashboard?tab=favoritos" className="flex items-center gap-1.5 text-sm font-semibold text-ink-700 hover:text-heat-700" data-testid="nav-favorites">
                <Heart className="w-4 h-4" />
                {favoritesCount > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-heat-100 text-heat-700 text-[10px] font-bold">
                    {favoritesCount}
                  </span>
                )}
              </Link>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-sm font-semibold text-ink-500 hover:text-ink-900"
                data-testid="nav-logout"
              >
                Sair
              </button>
              {showPublishCta && (
                <Link to="/publicar" className="btn-primary text-sm py-2.5 px-5" data-testid="nav-publish-cta">
                  Anunciar
                </Link>
              )}
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-semibold text-ink-700 hover:text-heat-700" data-testid="nav-login">
                Entrar
              </Link>
              <Link to="/publicar" className="btn-primary text-sm py-2.5 px-5" data-testid="nav-publish-cta">
                Anunciar
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-xl border border-cream-200 bg-white/60 backdrop-blur-sm text-ink-900"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
          aria-expanded={open}
          aria-controls="mobile-navigation"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div id="mobile-navigation" className="md:hidden border-t border-cream-200/60 bg-cream-50/80 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
          <div className="container-custom py-5 flex flex-col gap-2">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="font-semibold text-ink-800 py-2.5 hover:text-heat-700"
                onClick={() => setOpen(false)}
              >
                {l.label}
              </Link>
            ))}
            <button
              onClick={() => { toggleTheme(); setOpen(false); }}
              className="flex items-center gap-2 font-semibold text-ink-800 py-2.5"
            >
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              <span>Alternar Cor</span>
            </button>
            <div className="h-px bg-cream-200 my-2" />
            {isLoggedIn ? (
              <>
                {showDashboardLink && (
                  <Link to="/dashboard" className="font-semibold text-ink-800 py-2.5" onClick={() => setOpen(false)}>
                    Painel
                  </Link>
                )}
                {isAdminAccount && (
                  <Link to="/admin" className="font-semibold text-heat-700 py-2.5" onClick={() => setOpen(false)}>
                    Admin
                  </Link>
                )}
                <button
                  onClick={async () => { await logout(); setOpen(false); navigate("/"); }}
                  className="text-left font-semibold text-ink-500 py-2.5"
                >
                  Sair
                </button>
              </>
            ) : (
              <Link to="/login" className="font-semibold text-ink-800 py-2.5" onClick={() => setOpen(false)}>
                Entrar
              </Link>
            )}
            {showPublishCta && (
              <Link to="/publicar" className="btn-primary text-sm w-full mt-2" onClick={() => setOpen(false)}>
                Anunciar agora
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}